import React, { useState, useMemo } from 'react';
import { Clock, ChevronDown, Building2, Filter } from 'lucide-react';
import CompanyLogo from './CompanyLogo';
import { relativeTime, absoluteDate } from '../utils/format';

function formatDuration(ms) {
  return (ms / 1000).toFixed(1);
}

function RunsView({ runLogs }) {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [expandedRunId, setExpandedRunId] = useState(null);

  const stats = useMemo(() => {
    const companies = [...new Set(runLogs.map(run => run.company))];
    const totalRecords = runLogs.reduce((sum, run) => sum + run.records_added, 0);
    const lastRun = runLogs[0];
    return { companies, totalRecords, lastRun };
  }, [runLogs]);

  const filteredRuns = useMemo(() => {
    if (!selectedCompany) return runLogs;
    return runLogs.filter(run => run.company === selectedCompany);
  }, [runLogs, selectedCompany]);

  if (runLogs.length === 0) {
    return (
      <>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-stone-900">Timeline</h1>
          <p className="text-sm text-stone-500 mt-1">Collection run history across monitored companies</p>
        </div>
        <div className="bg-white rounded-xl p-10 border border-stone-200 text-center">
          <Clock size={32} className="mx-auto text-stone-300 mb-3" />
          <p className="text-stone-600 font-medium">No collection runs yet</p>
          <p className="text-sm text-stone-400 mt-1">Runs will appear here once the scraper starts collecting data</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-stone-900">Timeline</h1>
        <p className="text-sm text-stone-500 mt-1">Recent data collection activity</p>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 px-5 py-4 mb-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <div>
            <p className="text-xs text-stone-400 uppercase tracking-wide">Total runs</p>
            <p className="font-semibold text-stone-900 mt-0.5">{runLogs.length}</p>
          </div>
          <div>
            <p className="text-xs text-stone-400 uppercase tracking-wide">Records added</p>
            <p className="font-semibold text-green-700 mt-0.5">{stats.totalRecords}</p>
          </div>
          <div>
            <p className="text-xs text-stone-400 uppercase tracking-wide">Companies</p>
            <p className="font-semibold text-stone-900 mt-0.5">{stats.companies.length}</p>
          </div>
          {stats.lastRun && (
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wide">Last run</p>
              <p className="font-semibold text-stone-900 mt-0.5">{relativeTime(stats.lastRun.run_at)}</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Filter size={14} className="text-stone-400" />
          <select
            value={selectedCompany}
            onChange={e => setSelectedCompany(e.target.value)}
            className="text-sm bg-stone-50 border border-stone-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="">All companies</option>
            {stats.companies.map(company => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="hidden md:grid grid-cols-[minmax(140px,1fr)_minmax(0,2fr)_80px_80px_100px] gap-4 px-5 py-3 bg-stone-50 border-b border-stone-200 text-xs font-semibold uppercase tracking-wide text-stone-500">
          <span>Company</span>
          <span>Query</span>
          <span>Found</span>
          <span>Added</span>
          <span>When</span>
        </div>

        <div className="divide-y divide-stone-100">
          {filteredRuns.slice(0, 40).map(run => {
            const expanded = expandedRunId === run.id;
            return (
              <div key={run.id}>
                <button
                  type="button"
                  onClick={() => setExpandedRunId(expanded ? null : run.id)}
                  className="w-full text-left grid grid-cols-1 md:grid-cols-[minmax(140px,1fr)_minmax(0,2fr)_80px_80px_100px] gap-3 md:gap-4 px-5 py-4 items-center hover:bg-stone-50/80 transition"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <CompanyLogo name={run.company} size="sm" />
                    <span className="text-sm font-semibold text-stone-900 truncate">{run.company}</span>
                  </div>
                  <p className="text-sm text-stone-600 truncate">{run.query}</p>
                  <span className="text-sm font-medium text-stone-700">{run.results_found}</span>
                  <span className="text-sm font-semibold text-green-700">{run.records_added}</span>
                  <div className="flex items-center justify-between md:justify-start gap-2">
                    <span className="text-xs text-stone-500">{run.run_at ? relativeTime(run.run_at) : '—'}</span>
                    <ChevronDown size={14} className={`text-stone-400 transition md:hidden ${expanded ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {expanded && (
                  <div className="px-5 pb-4 bg-stone-50/60 border-t border-stone-100">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                      <div>
                        <p className="text-[10px] font-semibold text-stone-400 uppercase">Category</p>
                        <p className="text-stone-800 mt-0.5">{run.category}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-stone-400 uppercase">Adapter</p>
                        <p className="text-stone-800 mt-0.5">{run.adapter}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-stone-400 uppercase">Duration</p>
                        <p className="text-stone-800 mt-0.5">{formatDuration(run.duration_ms)}s</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-stone-400 uppercase">Run time</p>
                        <p className="text-stone-800 mt-0.5">{run.run_at ? absoluteDate(run.run_at) : '—'}</p>
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mt-3 flex items-center gap-1.5">
                      <Building2 size={12} /> Full query: <span className="text-stone-700 font-medium">{run.query}</span>
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default RunsView;